import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Dialog,
  HStack,
  IconButton,
  Portal,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Button as ButtonComponent } from '@themes/aegix';
import { HiClipboardCheck, HiClipboardCopy } from 'react-icons/hi';
import { toaster } from '../../ui/toaster';

// One-time reveal of a plaintext bearer token. Shared by the create
// flow (server auto-generates on first save) and the rotate flow
// (BearerRotateModal). Tiaki only persists the SHA-256 hash; once this
// modal closes the plaintext is gone.

export interface BearerRevealModalProps {
  token: string | null;
  variant: 'new' | 'rotated';
  onClose: () => void;
}

export function BearerRevealModal({
  token,
  variant,
  onClose,
}: BearerRevealModalProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  const handleCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
      toaster.create({
        title: t('common.copied', 'Copied'),
        description: t('sso.tokenCopiedClipboard', 'Token copied to clipboard.'),
        type: 'success',
        duration: 2000,
      });
    } catch (err) {
      toaster.create({
        title: t('sso.copyFailed', 'Copy failed'),
        description: (err as Error).message,
        type: 'error',
        duration: 4000,
      });
    }
  };

  return (
    <Dialog.Root
      open={token !== null}
      onOpenChange={({ open }) => {
        if (!open) handleClose();
      }}
      role="alertdialog"
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content data-e2e="sso-bearer-reveal-modal">
            <Dialog.Header>
              <Dialog.Title>
                {variant === 'new'
                  ? t('sso.bearerToken', 'Bearer token')
                  : t('sso.newBearerToken', 'New bearer token')}
              </Dialog.Title>
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={3}>
                <Text fontSize="sm" color="text.primary">
                  {t(
                    'sso.bearerTokenCopyNotice',
                    "Copy this token now and paste it into your IdP's SCIM configuration. Tiaki only stores the SHA-256 hash; this plaintext value will not be shown again.",
                  )}
                </Text>
                <HStack gap={2} align="stretch">
                  <Box
                    flex={1}
                    bg="surface.input"
                    borderWidth="1px"
                    borderColor="border.secondary"
                    borderRadius="md"
                    color="text.primary"
                    px={3}
                    py={2}
                    fontFamily="mono"
                    fontSize="sm"
                    wordBreak="break-all"
                    data-e2e="sso-bearer-reveal-value"
                  >
                    {token}
                  </Box>
                  <IconButton
                    aria-label={t('sso.copyBearerToken', 'Copy bearer token')}
                    onClick={handleCopy}
                    variant="outline"
                    size="sm"
                    data-e2e="sso-bearer-reveal-copy"
                  >
                    {copied ? <HiClipboardCheck /> : <HiClipboardCopy />}
                  </IconButton>
                </HStack>
                {variant === 'rotated' ? (
                  <Text fontSize="xs" color="surface.danger">
                    {t(
                      'sso.previousTokenInvalidatedNotice',
                      'The previous token has been invalidated. The IdP will fail SCIM pushes until the new token is configured there.',
                    )}
                  </Text>
                ) : null}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <ButtonComponent
                visual="primary"
                onClick={handleClose}
                data-e2e="sso-bearer-reveal-done"
              >
                {t('common.done', 'Done')}
              </ButtonComponent>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}

export default BearerRevealModal;
